import type { CSSProperties } from 'react';
import { cn } from '../../../utils/cn';

interface TeamColourChipProps {
  team: { display_name: string; color?: string | null; domain?: string | null };
  size?: 'sm' | 'md';
  /** Show the domain suffix for non-PlayCDU teams. */
  showDomain?: boolean;
  className?: string;
  title?: string;
}

const HEX = /^#?([0-9a-f]{6}|[0-9a-f]{3})$/i;

/** Inline styles for a Tesseract team colour (hex from the API); accent classes when absent/invalid. */
export function teamColourStyle(color: string | null | undefined): CSSProperties | undefined {
  if (!color || !HEX.test(color)) return undefined;
  const hex = color.startsWith('#') ? color : `#${color}`;
  return { color: hex, borderColor: `${hex}66`, backgroundColor: `${hex}1f` };
}

/** Team chip tinted with the team's own colour so a roster reads like the Discord role list. */
export const TeamColourChip = ({ team, size = 'sm', showDomain = false, className, title }: TeamColourChipProps) => {
  const style = teamColourStyle(team.color);
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full font-semibold border whitespace-nowrap',
        size === 'sm' ? 'h-5 px-2 text-[10px]' : 'h-6 px-2.5 text-xs',
        !style && 'bg-accent-500/15 text-accent-300 border-accent-500/25',
        className,
      )}
      style={style}
      title={title}
    >
      {team.display_name}
      {showDomain && team.domain && team.domain !== 'playcdu' && <span className="ml-1 opacity-70">· {team.domain}</span>}
    </span>
  );
};
